import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface RouteOptions {
  waypoints: number[][];
  optimize?: boolean;
  routeType?: 'fastest' | 'shortest' | 'eco';
  avoid?: string[];
  traffic?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class RouteService {
  private readonly subscriptionKey = '65TAwBgYcORpiWAKdmSK7ek1lv75sxr2-nWJuAGGBb0';
  private readonly baseUrl = 'https://atlas.microsoft.com/route/directions/json';

  constructor(private http: HttpClient) {}

  /**
   * Fetches route information from the Azure Maps Route API.
   */
  getRoute(options: RouteOptions): Observable<any> {
    if (!options.waypoints || options.waypoints.length < 2) {
      console.error('At least 2 waypoints are required for a route');
      return of(null);
    }

    // Build the querystring for waypoints (format: lat,lon:lat,lon:...)
    const waypointsQuery = options.waypoints
      .map(point => `${point[1]},${point[0]}`) // NOTE: Azure Maps API expects lat,lon format
      .join(':');

    const params = {
      'api-version': '1.0',
      'subscription-key': this.subscriptionKey,
      query: waypointsQuery,
      routeType: options.routeType || 'fastest',
      traffic: options.traffic ? 'true' : 'false',
      computeBestOrder: options.optimize ? 'true' : 'false',
      routeRepresentation: 'polyline',
      travelMode: 'car',
    };

    if (options.avoid && options.avoid.length) {
      params['avoid'] = options.avoid.join(',');
    }

    return this.http.get(this.baseUrl, { params }).pipe(
      catchError(error => {
        console.error('Error fetching route:', error);
        return of(null);
      })
    );
  }

  /**
   * Processes the route response into points for a map.
   */
  processRouteResponse(response: any): {
    routePoints: number[][];
    distance: number;
    duration: number;
    optimizedWaypoints?: number[];
  } | null {
    if (!response || !response.routes || !response.routes.length) {
      return null;
    }

    const route = response.routes[0];
    const routePoints: number[][] = [];

    // Extract route points from the legs
    route.legs.forEach(leg => {
      leg.points.forEach(point => {
        routePoints.push([point.longitude, point.latitude]);
      });
    });

    // Extract the optimized waypoint order if available
    const optimizedWaypoints = response.optimizedWaypoints || route.optimizedWaypoints || null;

    return {
      routePoints,
      distance: route.summary.lengthInMeters / 1000, // Convert to kilometres
      duration: route.summary.travelTimeInSeconds, // In seconds
      optimizedWaypoints,
    };
  }

  /**
   * Converts seconds to a readable hours/minutes format.
   */
  formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}u ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }
}
